import { Group, LoadingOverlay, Pagination, Stack, Table, Text } from '@mantine/core';

import { useMediaQuery } from '@mantine/hooks';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { useWebConnectorStore } from '@/store/webConnector';
import { ConnectorListItem } from './ConnectorListItem';

export const WebConnectorsList = () => {
  const { t } = useTranslation();
  const { datasetId } = useParams<{ datasetId: string }>();

  const navigate = useNavigate();
  const location = useLocation();
  const matches = useMediaQuery('(min-width: 640px)');

  const {
    connectors,
    loading,
    error,
    fetchConnectors,
    selectedConnector,
    selectConnector,
    sortBy,
    reverseSortDirection,
    setSorting,
  } = useWebConnectorStore();

  useEffect(() => {
    if (datasetId) {
      fetchConnectors({
        datasetId,
        page: 1,
        size: connectors.size,
        order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
      });
    }
  }, [datasetId, fetchConnectors]);

  const handleViewConnector = (connectorId: string, status?: string) => {
    const connector = connectors.items.find((item) => item.id === connectorId);
    if (connector) {
      selectConnector(connector);
      // 截取当前路径并添加connectorId
      const currentPath = location.pathname; // 例如: /bot/xxx/datasets/xxx/data-source/web-connector
      navigate(`${currentPath}/${connectorId}${status ? `?status=${status}` : ''}`);
    }
  };

  const handlePageChange = (page: number) => {
    if (datasetId && page !== connectors.page) {
      fetchConnectors({
        datasetId,
        page,
        size: connectors.size,
        order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
      });
    }
  };

  return (
    <section className="flex flex-col relative h-full">
      {/* 如需一些筛选操作可在此添加 */}
      <section className="flex-auto">
        <section className="bg-white rounded-lg shadow-sm h-full flex flex-col">
          <div className="flex-auto overflow-auto">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('dataset.web_connector.name')}</Table.Th>
                  <Table.Th>{t('dataset.web_connector.source_address')}</Table.Th>
                  <Table.Th>{t('dataset.web_connector.source_count')}</Table.Th>
                  <Table.Th>{t('embeddings.table.status')}</Table.Th>
                  <Table.Th>{t('dataset.web_connector.last_refresh')}</Table.Th>
                  <Table.Th>{t('dataset.web_connector.refresh_frequency')}</Table.Th>
                  <Table.Th>{t('dataset.web_connector.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                <LoadingOverlay visible={loading} />
                {connectors.items.length === 0 && !loading ? (
                  <Table.Tr>
                    <Table.Td colSpan={6} className="text-center py-8">
                      <Stack align="center" gap="md">
                        <Text c="gray.5">{t('no_data')}</Text>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  connectors.items.map((connector) => (
                    <ConnectorListItem
                      key={connector.id}
                      connector={connector}
                      onView={(status) => handleViewConnector(connector.id, status)}
                    />
                  ))
                )}
              </Table.Tbody>
            </Table>
          </div>

          {/* 分页区域 */}
          {connectors.pages > 1 && (
            <div className="flex-none p-4 border-t">
              <Group justify="center">
                <Pagination
                  value={connectors.page}
                  total={connectors.pages}
                  size={matches ? 'md' : 'sm'}
                  onChange={handlePageChange}
                />
              </Group>
            </div>
          )}
        </section>
      </section>
    </section>
  );
};
